import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Card,
  Button,
  Typography,
  Space,
  List,
  Tag,
  Empty,
  Spin,
  Divider,
  Row,
  Col,
  message,
  Segmented,
  Image,
  Drawer,
} from 'antd';
import {
  ToolOutlined,
  ArrowLeftOutlined,
  CarOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import PublicLayout from '../components/landing/PublicLayout';
import { useCustomerAuth } from '../contexts/CustomerAuthContext';
import { customerPortalApi } from '../services/apiInstances';
import { SlotPicker } from '../components/Scheduling/SlotPicker';

const { Title, Text } = Typography;

const statusColor = (status?: string): string => {
  switch ((status || '').toLowerCase()) {
    case 'completed':
    case 'invoiced':
      return 'green';
    case 'cancelled':
      return 'red';
    case 'in_progress':
    case 'scheduled':
      return 'blue';
    default:
      return 'default';
  }
};

const money = (value: any): string => {
  const n = Number(value);
  return `$${(Number.isFinite(n) ? n : 0).toFixed(2)}`;
};

interface LinePhoto {
  url: string;
  caption?: string;
}

interface LineUrgency {
  id: number;
  label: string;
  color: string;
}

interface LineItem {
  id: number;
  // Legacy / parts schema
  description?: string;
  category?: string;
  quantity?: number;
  total_price?: number;
  // 3C work-order schema (defensive fallbacks)
  concern?: string;
  correction?: string;
  line_total?: number;
  // Self-service enrichment
  urgency?: LineUrgency | null;
  customer_approval_status?: string;
  photos?: LinePhoto[];
}

type Decision = 'approved' | 'declined' | 'deferred';

const DECISION_OPTIONS = [
  { label: 'Approve', value: 'approved' },
  { label: 'Decline', value: 'declined' },
  { label: 'Defer', value: 'deferred' },
];

const normalizeDecision = (status?: string): Decision | undefined => {
  switch ((status || '').toLowerCase()) {
    case 'approved':
      return 'approved';
    case 'declined':
      return 'declined';
    case 'deferred':
      return 'deferred';
    default:
      return undefined;
  }
};

interface TimeEntry {
  id: number;
  description?: string;
  hours?: number;
  created_at?: string;
  first_name?: string;
  last_name?: string;
}

const CustomerWorkOrderDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, accessToken } = useCustomerAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [workOrder, setWorkOrder] = useState<any>(null);
  const [lineItems, setLineItems] = useState<LineItem[]>([]);
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [inspectionPhotos, setInspectionPhotos] = useState<LinePhoto[]>([]);
  // Edited decisions keyed by lineItemId; seeded from saved customer_approval_status on load.
  const [decisions, setDecisions] = useState<Record<number, Decision>>({});
  const [pickOpen, setPickOpen] = useState(false);

  const fetchAvailability = useCallback(
    async (startDate: string, endDate: string) => {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const res = await customerPortalApi.get(
        `/customer-portal/work-orders/${id}/availability?start_date=${startDate}&end_date=${endDate}`,
        { headers }
      );
      return res.data;
    },
    [accessToken, id]
  );

  const submitRequest = useCallback(
    async (startTime: string) => {
      const headers = { Authorization: `Bearer ${accessToken}` };
      await customerPortalApi.post(
        `/customer-portal/work-orders/${id}/request-appointment`,
        { start_time: startTime },
        { headers }
      );
      message.success('Time requested — the shop will confirm shortly');
      setPickOpen(false);
    },
    [accessToken, id]
  );

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      const res = await customerPortalApi.get(`/customer-portal/work-orders/${id}`, { headers });
      const items: LineItem[] = res.data.lineItems || [];
      setWorkOrder(res.data.workOrder || null);
      setLineItems(items);
      setTimeEntries(res.data.timeEntries || []);
      setInspectionPhotos(res.data.inspectionPhotos || []);
      // Seed local decision state from the persisted statuses.
      const seeded: Record<number, Decision> = {};
      items.forEach((it) => {
        const d = normalizeDecision(it.customer_approval_status);
        if (d) seeded[it.id] = d;
      });
      setDecisions(seeded);
    } catch (error) {
      message.error('Could not load this work order.');
    } finally {
      setLoading(false);
    }
  }, [accessToken, id]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/customer/login');
      return;
    }
    load();
  }, [isAuthenticated, navigate, load]);

  const setDecision = (lineItemId: number, value: Decision) => {
    setDecisions((prev) => ({ ...prev, [lineItemId]: value }));
  };

  const submitDecisions = async () => {
    const payload = Object.entries(decisions).map(([lineItemId, status]) => ({
      lineItemId: Number(lineItemId),
      status,
    }));
    if (payload.length === 0) {
      message.info('Choose Approve, Decline, or Defer on at least one item first.');
      return;
    }
    setSubmitting(true);
    try {
      const headers = { Authorization: `Bearer ${accessToken}` };
      await customerPortalApi.patch(
        `/customer-portal/work-orders/${id}/line-decisions`,
        { decisions: payload },
        { headers }
      );
      message.success('Your decisions were sent to the shop.');
      await load();
    } catch (error) {
      message.error('Could not submit your decisions. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PublicLayout>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <Spin size="large" />
        </div>
      </PublicLayout>
    );
  }

  const lineTitle = (item: LineItem) =>
    item.description || item.concern || item.category || 'Service item';
  const lineAmount = (item: LineItem): number => {
    const raw = item.total_price != null ? item.total_price : item.line_total;
    const n = Number(raw);
    return Number.isFinite(n) ? n : 0;
  };

  // Approved-subtotal preview recomputes from the current (edited) decisions.
  const approvedSubtotal = lineItems.reduce(
    (sum, item) => (decisions[item.id] === 'approved' ? sum + lineAmount(item) : sum),
    0
  );
  // Allow self-scheduling while work is still upcoming/active (not finished/closed).
  const closedStatuses = ['completed', 'invoiced', 'cancelled'];
  const isSchedulable =
    !!workOrder && !closedStatuses.includes((workOrder.status || '').toLowerCase());

  const taxRate = Number(workOrder?.tax_rate);
  // tax_rate is stored as a percentage (e.g. 7 = 7%), matching backend recalcTotals.
  const approvedTax = Number.isFinite(taxRate) ? approvedSubtotal * (taxRate / 100) : 0;
  const approvedTotal = approvedSubtotal + approvedTax;

  return (
    <PublicLayout>
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #f0f2f5 0%, #ffffff 100%)',
        padding: '40px 20px 96px'
      }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          {/* Header */}
          <Card style={{ marginBottom: 24 }}>
            <Space direction="vertical" size={0} style={{ width: '100%' }}>
              <Button
                type="link"
                icon={<ArrowLeftOutlined />}
                onClick={() => navigate('/customer/history')}
                style={{ padding: 0, marginBottom: 8 }}
              >
                Back to History
              </Button>
              {!workOrder ? null : (
                <Row align="middle" justify="space-between" gutter={[8, 8]}>
                  <Col>
                    <Title level={3} style={{ margin: 0 }}>
                      <ToolOutlined /> Work Order {workOrder.work_order_number}
                    </Title>
                    <Text type="secondary">
                      <CarOutlined /> {workOrder.year} {workOrder.make} {workOrder.model}
                    </Text>
                  </Col>
                  <Col>
                    <Space direction="vertical" align="end" size={8}>
                      <Tag color={statusColor(workOrder.status)} style={{ fontSize: 14 }}>
                        {(workOrder.status || 'unknown').toUpperCase()}
                      </Tag>
                      {isSchedulable && (
                        <Button
                          type="primary"
                          icon={<ClockCircleOutlined />}
                          onClick={() => setPickOpen(true)}
                        >
                          Schedule visit
                        </Button>
                      )}
                    </Space>
                  </Col>
                </Row>
              )}
            </Space>
          </Card>

          {!workOrder ? (
            <Card>
              <Empty description="Work order not found" image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </Card>
          ) : (
            <>
              {/* Inspection photos (DVI diagnosis evidence) */}
              {inspectionPhotos.length > 0 && (
                <Card title="Inspection Photos" style={{ marginBottom: 24 }}>
                  <Image.PreviewGroup>
                    <Space size={8} wrap>
                      {inspectionPhotos.map((photo, idx) => (
                        <div key={idx} style={{ width: 96 }}>
                          <Image
                            src={photo.url}
                            alt={photo.caption || 'Inspection photo'}
                            width={96}
                            height={96}
                            style={{ objectFit: 'cover', borderRadius: 6 }}
                          />
                          {photo.caption && (
                            <Text
                              type="secondary"
                              style={{ fontSize: 11, display: 'block', marginTop: 2 }}
                            >
                              {photo.caption}
                            </Text>
                          )}
                        </div>
                      ))}
                    </Space>
                  </Image.PreviewGroup>
                </Card>
              )}

              {/* Line items */}
              <Card title="Line Items" style={{ marginBottom: 24 }}>
                <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>
                  Review each item and choose Approve, Decline, or Defer. You can change these
                  anytime; contact the shop to change confirmed work.
                </Text>
                {lineItems.length > 0 ? (
                  <List
                    dataSource={lineItems}
                    renderItem={(item) => (
                      <List.Item style={{ display: 'block' }}>
                        <Row justify="space-between" align="top" gutter={[8, 8]} wrap>
                          <Col flex="auto">
                            <Space direction="vertical" size={4} style={{ width: '100%' }}>
                              <Space size="small" wrap>
                                <Text strong>{lineTitle(item)}</Text>
                                {item.urgency && (
                                  <Tag color={item.urgency.color}>{item.urgency.label}</Tag>
                                )}
                              </Space>
                              <Space size="small" wrap>
                                {item.category && <Tag>{item.category}</Tag>}
                                {item.quantity != null && (
                                  <Text type="secondary">Qty: {item.quantity}</Text>
                                )}
                                {item.correction && (
                                  <Text type="secondary">{item.correction}</Text>
                                )}
                              </Space>
                            </Space>
                          </Col>
                          <Col>
                            <Text strong>{money(lineAmount(item))}</Text>
                          </Col>
                        </Row>

                        {/* Photo thumbnails for this line */}
                        {item.photos && item.photos.length > 0 && (
                          <div style={{ marginTop: 8 }}>
                            <Image.PreviewGroup>
                              <Space size={8} wrap>
                                {item.photos.map((photo, idx) => (
                                  <div key={idx} style={{ width: 80 }}>
                                    <Image
                                      src={photo.url}
                                      alt={photo.caption || 'Photo'}
                                      width={80}
                                      height={80}
                                      style={{ objectFit: 'cover', borderRadius: 6 }}
                                    />
                                    {photo.caption && (
                                      <Text
                                        type="secondary"
                                        style={{ fontSize: 11, display: 'block', marginTop: 2 }}
                                      >
                                        {photo.caption}
                                      </Text>
                                    )}
                                  </div>
                                ))}
                              </Space>
                            </Image.PreviewGroup>
                          </div>
                        )}

                        {/* Approve / Decline / Defer control */}
                        <div style={{ marginTop: 12, maxWidth: '100%', overflowX: 'auto' }}>
                          <Segmented
                            options={DECISION_OPTIONS}
                            // '' = no decision yet, so AntD highlights nothing on a
                            // pending line and the first tap on Approve fires onChange.
                            value={decisions[item.id] ?? ''}
                            onChange={(val) => setDecision(item.id, val as Decision)}
                          />
                        </div>
                      </List.Item>
                    )}
                  />
                ) : (
                  <Empty description="No line items" image={Empty.PRESENTED_IMAGE_SIMPLE} />
                )}
              </Card>

              {/* Time entries */}
              {timeEntries.length > 0 && (
                <Card
                  title={<Space><ClockCircleOutlined /><span>Labor Log</span></Space>}
                  style={{ marginBottom: 24 }}
                >
                  <List
                    dataSource={timeEntries}
                    renderItem={(entry) => (
                      <List.Item>
                        <List.Item.Meta
                          title={<Text>{entry.description || 'Work performed'}</Text>}
                          description={
                            <Space size="small" wrap>
                              {(entry.first_name || entry.last_name) && (
                                <Text type="secondary">
                                  {[entry.first_name, entry.last_name].filter(Boolean).join(' ')}
                                </Text>
                              )}
                              {entry.created_at && (
                                <Text type="secondary">
                                  {dayjs(entry.created_at).format('MMM D, YYYY')}
                                </Text>
                              )}
                            </Space>
                          }
                        />
                        {entry.hours != null && <Text strong>{entry.hours} hrs</Text>}
                      </List.Item>
                    )}
                  />
                </Card>
              )}

              {/* Totals */}
              <Card title="Summary">
                <Space direction="vertical" style={{ width: '100%' }} size="small">
                  <Row justify="space-between">
                    <Text type="secondary">Subtotal</Text>
                    <Text>{money(workOrder.subtotal)}</Text>
                  </Row>
                  <Row justify="space-between">
                    <Text type="secondary">Tax</Text>
                    <Text>{money(workOrder.tax)}</Text>
                  </Row>
                  <Divider style={{ margin: '8px 0' }} />
                  <Row justify="space-between">
                    <Text strong style={{ fontSize: 18 }}>Total</Text>
                    <Text strong style={{ fontSize: 18 }}>{money(workOrder.total_amount)}</Text>
                  </Row>
                  {workOrder.completed_at && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Completed {dayjs(workOrder.completed_at).format('MMM D, YYYY')}
                    </Text>
                  )}
                </Space>
              </Card>
            </>
          )}
        </div>
      </div>

      {/* Sticky approved-subtotal / submit bar */}
      {workOrder && lineItems.length > 0 && (
        <div
          style={{
            position: 'sticky',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            background: '#ffffff',
            borderTop: '1px solid #f0f0f0',
            boxShadow: '0 -2px 8px rgba(0,0,0,0.08)',
            padding: '12px 16px',
          }}
        >
          <div style={{ maxWidth: 900, margin: '0 auto' }}>
            <Row align="middle" justify="space-between" gutter={[8, 8]} wrap>
              <Col flex="auto">
                <Space direction="vertical" size={0}>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Approved subtotal{Number.isFinite(taxRate) ? ' + tax' : ''}
                  </Text>
                  <Text strong style={{ fontSize: 18 }}>
                    {money(approvedTotal)}
                  </Text>
                </Space>
              </Col>
              <Col>
                <Button
                  type="primary"
                  size="large"
                  loading={submitting}
                  onClick={submitDecisions}
                >
                  Submit decisions
                </Button>
              </Col>
            </Row>
          </div>
        </div>
      )}

      <Drawer
        title="Schedule your visit"
        open={pickOpen}
        onClose={() => setPickOpen(false)}
        width={typeof window !== 'undefined' && window.innerWidth <= 768 ? '100%' : 480}
        destroyOnHidden
      >
        {pickOpen && (
          <SlotPicker
            title="Pick a time"
            fetchAvailability={fetchAvailability}
            submit={submitRequest}
            confirmLabel="Request"
          />
        )}
      </Drawer>
    </PublicLayout>
  );
};

export default CustomerWorkOrderDetail;
